import { useEffect, useState } from 'react';
import type { ApiClient, OhneGeschoss } from '../../api';

/**
 * Der Hinweis auf Aussparungen ohne Geschoss - sie stehen auf keinem Blatt.
 * Bis v0.45.1 fielen sie aus der Auswahl still heraus (Bestand 8000: 186 an
 * der Bezugsebene „-G00 100-jähriges Hochwasser"). NULL, wenn es keine gibt.
 */
export function ohneGeschossText(o: OhneGeschoss | null | undefined): string | null {
  if (!o || o.stueck <= 0) return null;

  const ebenen = o.ebenen
    .map((e) => `„${e.ebene}" ${e.stueck}`)
    .join(', ');

  return `${o.stueck} ${o.stueck === 1 ? 'Aussparung' : 'Aussparungen'} ohne Geschoss `
    + `— auf keinem Plan (Ebene im Modell: ${ebenen})`;
}

/**
 * Der PDF-Knopf des Aussparungsplans (AS7) — je Geschoss und je
 * Planungsbereich eines Geschosses.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DAS GESCHOSS IST PFLICHT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Anders als bei der Anforderungsliste gibt es hier KEIN „alle Geschosse".
 * Die Liste darf sieben Ebenen untereinander drucken; ein Grundriss darf sie
 * nicht übereinander zeichnen. Gemessen am 23.09.2026: Jeder der sechs
 * Planungsbereiche spannt alle sieben Geschosse — ein Blatt „Abschnitt 2"
 * legte 1.342 Aussparungen aus sieben Höhen auf dieselben Koordinaten, und
 * niemand sähe es dem Blatt an.
 *
 * Deshalb steht in der Auswahl jedes der 49 Blätter einzeln, mit seiner Zahl.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE ZAHL STEHT AM WÄHLER, NICHT ERST IM PDF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Dasselbe Muster wie beim Knopf der Anforderungsliste: Wer erst nach dem
 * Erzeugen sieht, dass ein Ausschnitt leer ist, hat ein Dokument erzeugt und
 * eine Registernummer verbraucht. Ausschnitte ohne Aussparung stehen gar
 * nicht erst zur Wahl — der Endpunkt liefert nur, was er gezählt hat.
 */
export function AussparungsplanKnopf({
  api, projektId, bereich,
}: {
  api: ApiClient;
  projektId: string;
  /** Der gewählte Planungsbereich, wenn einer gewählt ist. */
  bereich?: string;
}) {
  const [geschosse, setGeschosse] = useState<
    { geschoss: string; stueck: number }[]
  >([]);

  const [bereiche, setBereiche] = useState<
    { bereich: string; geschoss: string; stueck: number }[]
  >([]);

  const [ohneGeschoss, setOhneGeschoss] = useState<OhneGeschoss | null>(null);

  const [gewaehlt, setGewaehlt] = useState<string>('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.aussparungPlanblaetter(projektId)
      .then((x) => {
        setGeschosse(x.geschosse ?? []);
        setBereiche(x.bereiche ?? []);
        setOhneGeschoss(x.ohneGeschoss ?? null);
      })
      // Ohne Liste bleibt der Knopf ohne Wahl - und damit ohne Wirkung. Ein
      // Knopf, der ohne Geschoss ein Blatt zöge, zöge das falsche.
      .catch(() => { setGeschosse([]); setBereiche([]); setOhneGeschoss(null); });
  }, [api, projektId]);

  // Die Blätter dieses Ausschnitts: ohne Bereichswahl die sieben
  // Geschossblätter, mit Bereichswahl dessen sieben Geschosse.
  const auswahl = bereich
    ? bereiche
        .filter((b) => b.bereich === bereich)
        .map((b) => ({
          wert: b.geschoss,
          text: `${b.geschoss} — ${b.stueck} Aussparungen`,
        }))
    : geschosse.map((g) => ({
        wert: g.geschoss,
        text: `${g.geschoss} — ${g.stueck} Aussparungen`,
      }));

  const hole = async () => {
    if (!gewaehlt) return;

    setLaeuft(true);
    setFehler(null);

    try {
      await api.aussparungPlanPdf(projektId, gewaehlt, bereich || undefined);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center',
                   flexWrap: 'wrap' }}>
      <select
        value={gewaehlt}
        onChange={(e) => setGewaehlt(e.target.value)}
        aria-label="Geschoss für den Aussparungsplan"
      >
        <option value="">Plan: Geschoss wählen</option>
        {auswahl.map((a) => (
          <option key={a.wert} value={a.wert}>{a.text}</option>
        ))}
      </select>

      <button
        type="button"
        className="m-knopf"
        disabled={laeuft || !gewaehlt}
        onClick={hole}
        title={bereich
          ? `Planblatt ${bereich} · ${gewaehlt || 'Geschoss wählen'} mit `
            + 'eigener Dokumentnummer'
          : 'Ein Planblatt je Geschoss — Grundriss, Achsen, Sinnbilder mit '
            + 'Ampelfarbe, Maßstab aus der Normreihe'}
      >
        {laeuft ? 'erzeugt …' : 'Aussparungsplan als PDF'}
      </button>

      {fehler && (
        <span className="hinweis fehler" style={{ fontSize: 11 }}>{fehler}</span>
      )}

      {ohneGeschossText(ohneGeschoss) && (
        <span className="hinweis" style={{ fontSize: 11 }} data-ohne-geschoss="">
          {ohneGeschossText(ohneGeschoss)}
        </span>
      )}
    </span>
  );
}
